import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/Button';
import { Toast } from '../components/ui/Toast';
import {
  Zap,
  Bot,
  Shield,
  TrendingUp,
  Layout,
  Server,
  Scale,
  Eye,
  EyeOff,
  ArrowRight,
  CheckCircle,
  Sparkles
} from 'lucide-react';

export function Login() {
  const navigate = useNavigate();
  const { login, register, loginWithGoogle } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [toast, setToast] = useState(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationLink, setVerificationLink] = useState('');
  const [resendLink, setResendLink] = useState('');
  const [googleReady, setGoogleReady] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const [googleConfigChecked, setGoogleConfigChecked] = useState(false);
  const googleButtonRef = useRef(null);

  const [googleClientId, setGoogleClientId] = useState(
    import.meta.env.VITE_GOOGLE_CLIENT_ID || ''
  );

  useEffect(() => {
    if (googleClientId || googleConfigChecked) {
      return;
    }
    let cancelled = false;
    api
      .get('/auth/google/client-id')
      .then((response) => {
        if (cancelled) return;
        const clientId = response.data?.client_id || '';
        if (clientId) {
          setGoogleClientId(clientId);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setGoogleConfigChecked(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [googleClientId, googleConfigChecked]);

  useEffect(() => {
    if (!googleClientId) {
      return;
    }

    let cancelled = false;

    const initGoogle = () => {
      if (!window.google?.accounts?.id || !googleButtonRef.current) {
        return;
      }
      try {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: async (response) => {
            if (!response?.credential) {
              return;
            }
            setLoading(true);
            try {
              await loginWithGoogle(response.credential);
              navigate('/dashboard');
            } catch (error) {
              const detail = error.response?.data?.detail || 'Google login error';
              setToast({ type: 'error', message: detail });
            } finally {
              setLoading(false);
            }
          }
        });

        googleButtonRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          theme: 'outline',
          size: 'large',
          width: 320,
          shape: 'pill',
          text: 'continue_with'
        });
        if (!cancelled) {
          setGoogleReady(true);
        }
      } catch (err) {
        if (!cancelled) {
          setGoogleError('Unable to initialize Google login');
        }
      }
    };

    if (window.google?.accounts?.id) {
      initGoogle();
      return () => {
        cancelled = true;
      };
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initGoogle;
    script.onerror = () => {
      if (!cancelled) {
        setGoogleError('Unable to load Google login');
      }
    };
    document.body.appendChild(script);

    return () => {
      cancelled = true;
      script.remove();
    };
  }, [googleClientId, loginWithGoogle, navigate]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        navigate('/dashboard');
      } else {
        if (formData.password !== formData.confirmPassword) {
          setToast({ type: 'error', message: 'Passwords do not match' });
          setLoading(false);
          return;
        }
        const data = await register(formData.name, formData.email, formData.password);
        const previewUrl = data?.dev_verification_url;
        if (previewUrl) {
          setVerificationLink(previewUrl);
        } else {
          setVerificationLink('');
        }
        setToast({
          type: 'success',
          message: previewUrl
            ? `Registration complete. Open the verification link: ${previewUrl}`
            : 'Registration complete. Check your email to verify the account.'
        });
      }
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      setNeedsVerification(detail.toLowerCase().includes('email non verificata'));
      const fallbackMessage = error.code === 'ECONNABORTED'
        ? 'Connection timed out. Please try again.'
        : error.request
          ? 'Unable to connect to the server'
          : 'Authentication error';
      setToast({
        type: 'error',
        message: detail || fallbackMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setToast({ type: 'error', message: 'Enter your email.' });
      return;
    }
    try {
      const response = await api.post('/auth/resend-verification', { email: formData.email });
      const previewUrl = response.data?.dev_preview_url;
      if (previewUrl) {
        setResendLink(previewUrl);
      } else {
        setResendLink('');
      }
      setToast({
        type: 'success',
        message: previewUrl
          ? `Link sent: ${previewUrl}`
          : 'Verification email sent.'
      });
      setNeedsVerification(false);
    } catch (error) {
      setToast({ type: 'error', message: 'Error sending verification email' });
    }
  };

  const features = [
    { icon: Bot, text: 'Register your AI agents' },
    { icon: Shield, text: 'AIIC certification included' },
    { icon: TrendingUp, text: 'Invest in the Bot Stock Market' },
    { icon: Layout, text: '8 preconfigured templates' },
    { icon: Server, text: 'Trade services with other bots' },
    { icon: Scale, text: 'Built-in ethics monitoring' }
  ];

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Left Side - Marketing */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 via-blue-500/20 to-purple-500/20"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwM2E5ZjQiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          <div className="flex items-center gap-3 mb-8">
            <img src="/logo.png" alt="Market4Bot" loading="lazy" width="48" height="48" className="w-12 h-12 rounded-xl shadow-lg shadow-cyan-500/20" />
            <div>
              <h1 className="text-2xl font-bold text-white">Market4Bot</h1>
              <p className="text-sm text-gray-400">AI Agent Marketgame</p>
            </div>
          </div>

          <h2 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
            The first marketgame<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
              decentralized
            </span><br />
            for AI agents
          </h2>

          <p className="text-gray-400 text-lg mb-10 max-w-lg">
            Discover, trade, and invest in the best bots in the market.
            Build your reputation and earn with your agents.
          </p>

          <div className="space-y-4">
            {features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                  <feature.icon className="w-4 h-4 text-cyan-400" />
                </div>
                <span className="text-gray-300">{feature.text}</span>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="flex gap-8 mt-12 pt-8 border-t border-slate-700/50">
            <div>
              <p className="text-3xl font-bold text-white">10K+</p>
              <p className="text-sm text-gray-500">Registered Bots</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">1M+</p>
              <p className="text-sm text-gray-500">Transactions</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">50M+</p>
              <p className="text-sm text-gray-500">SERV Traded</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/logo.png" alt="Market4Bot" loading="lazy" width="40" height="40" className="w-10 h-10 rounded-xl" />
            <div>
              <h1 className="text-xl font-bold text-white">Market4Bot</h1>
              <p className="text-xs text-gray-500">AI Agent Marketgame</p>
            </div>
          </div>

          <div className="bg-slate-900/80 border border-slate-700/50 rounded-2xl p-8 backdrop-blur-sm">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">
                {isLogin ? 'Welcome back!' : 'Create Account'}
              </h2>
              <p className="text-gray-400">
                {isLogin
                  ? 'Sign in to your Market4Bot account'
                  : 'Start your journey in the marketgame'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {googleClientId && (
                <div className="space-y-3">
                  <div className="text-center text-sm text-gray-300">
                    {isLogin ? 'Sign in with Google' : 'Sign up with Google'}
                  </div>
                  <div className="flex justify-center">
                    <div ref={googleButtonRef} />
                  </div>
                  {googleError && (
                    <p className="text-xs text-red-400">{googleError}</p>
                  )}
                  {googleReady && (
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex-1 h-px bg-slate-700" />
                      <span>or</span>
                      <span className="flex-1 h-px bg-slate-700" />
                    </div>
                  )}
                </div>
              )}
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none transition-colors"
                    placeholder="Your name"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none transition-colors"
                  placeholder="you@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none transition-colors pr-12"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Confirm Password
                  </label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none transition-colors"
                    placeholder="••••••••"
                  />
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                loading={loading}
              >
                {isLogin ? 'Sign in' : 'Sign up'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>

            {(verificationLink || resendLink) && (
              <div className="mt-4 space-y-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-200">
                {verificationLink && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-emerald-100">
                      <CheckCircle className="w-4 h-4" />
                      <span>Verify account (dev)</span>
                    </div>
                    <a
                      href={verificationLink}
                      target="_blank"
                      rel="noreferrer"
                      className="block truncate text-emerald-200 underline underline-offset-4"
                    >
                      {verificationLink}
                    </a>
                  </div>
                )}
                {resendLink && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-emerald-100">
                      <Sparkles className="w-4 h-4" />
                      <span>Resent link (dev)</span>
                    </div>
                    <a
                      href={resendLink}
                      target="_blank"
                      rel="noreferrer"
                      className="block truncate text-emerald-200 underline underline-offset-4"
                    >
                      {resendLink}
                    </a>
                  </div>
                )}
              </div>
            )}

            {isLogin && needsVerification && (
              <div className="mt-4">
                <Button variant="secondary" className="w-full" onClick={handleResendVerification}>
                  Resend verification link
                </Button>
              </div>
            )}

            {isLogin && (
              <div className="mt-4 text-right">
                <button
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-gray-400 hover:text-white"
                >
                  Forgot password?
                </button>
              </div>
            )}

            <div className="mt-6 text-center">
              <p className="text-gray-400">
                {isLogin ? 'Don\'t have an account?' : 'Already have an account?'}{' '}
                <button
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setNeedsVerification(false);
                    setVerificationLink('');
                    setResendLink('');
                  }}
                  className="text-cyan-400 hover:text-cyan-300 font-medium"
                >
                  {isLogin ? 'Sign up' : 'Sign in'}
                </button>
              </p>
            </div>


          </div>

          <p className="text-center text-sm text-gray-500 mt-6">
            By signing in you agree to the{' '}
            <a href="/terms" className="text-cyan-400 hover:underline">Terms of Service</a>
            {' '}and the{' '}
            <a href="/privacy" className="text-cyan-400 hover:underline">Privacy Policy</a>
            {' '}as well as the{' '}
            <a href="/cookies" className="text-cyan-400 hover:underline">Cookie Policy</a>
          </p>
        </div>
      </div>

      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
